/**
 * Generic property animation track. Captures a property's value at keyframes,
 * interpolates between them (linear / cubic / step), applies back during
 * timeline playback or scrub. Implements AnimTrack so it slots into the
 * existing TimelinePanel / TrackManager infrastructure unchanged.
 *
 * One PropertyAnimTrack instance = one (element, effect) pair = one row in
 * the timeline (eventually).
 *
 * Value representation: everything flattens to a number[] so a single
 * CubicSpline can do the interpolation.
 *   - number → [v]
 *   - vec3   → [x, y, z]
 *   - color  → [r, g, b]
 *   - bool   → [v]  (interpolation forced to 'step' below)
 */

import { CubicSpline } from '../anim/spline';
import { AnimTrack } from '../anim-track';
import { Events } from '../events';

export type ValueKind = 'number' | 'vec3' | 'color' | 'bool';
export type Interpolation = 'linear' | 'cubic' | 'step';

export interface EffectAdapter<T = unknown> {
    /** Read current value from the target. Should return a fresh array, not aliased. */
    get(): number[];
    /** Apply interpolated value to the target. Receives the same-length flat array. */
    set(value: number[]): void;
    /** Bytes per keyframe (1 for number/bool, 3 for vec3/color). */
    readonly dim: number;
}

export interface PropertyAnimTrackOptions {
    events: Events;
    /** Unique id for this track (e.g. "splat:7:position") — used as event topic prefix. */
    id: string;
    adapter: EffectAdapter;
    interpolation: Interpolation;
    /** Optional callback after a value is applied (e.g. scene.forceRender = true). */
    onApply?: () => void;
    /** Optional identity / "no effect" value. When the track's last keyframe
     *  is removed the identity is written via adapter.set, restoring the
     *  effect to its no-op state. Skipped if undefined (the target keeps
     *  its last interpolated value). */
    identity?: number[];
    /** Optional callback fired the first time a keyframe is added (track
     *  transitions from empty → non-empty). Used by compound effects that
     *  need to flip an "enabled" flag elsewhere. */
    onActivate?: () => void;
    /** Optional callback fired when the last keyframe is removed (track
     *  transitions from non-empty → empty). Fires AFTER identity reset. */
    onDeactivate?: () => void;
}

interface Keyframe {
    frame: number;
    /** Captured value flattened to numbers. Length === adapter.dim. */
    value: number[];
}

class PropertyAnimTrack implements AnimTrack {
    private events: Events;
    private id: string;
    private adapter: EffectAdapter;
    private interp: Interpolation;
    private onApply?: () => void;
    private identity?: number[];
    private onActivateCb?: () => void;
    private onDeactivateCb?: () => void;
    private frames: Keyframe[] = [];
    private spline: CubicSpline | null = null;
    private scratch: number[] = [];
    private paused = false;
    // Stored EventHandle refs so we can subscribe/unsubscribe based on whether
    // we have any keyframes. A track with zero keys has ZERO impact on the
    // scene — no listeners attached, evaluate never runs.
    private timeHandle: any = null;
    private frameHandle: any = null;
    private smoothHandle: any = null;
    private pivotStartHandle: any = null;
    private pivotEndHandle: any = null;

    constructor(opts: PropertyAnimTrackOptions) {
        this.events = opts.events;
        this.id = opts.id;
        this.adapter = opts.adapter;
        this.interp = opts.interpolation;
        this.onApply = opts.onApply;
        this.identity = opts.identity ? opts.identity.slice() : undefined;
        this.onActivateCb = opts.onActivate;
        this.onDeactivateCb = opts.onDeactivate;
        this.scratch = new Array(this.adapter.dim).fill(0);

        // Pause this track while the user is actively dragging a transform
        // gizmo (between pivot.started and pivot.ended). Without this, a track
        // with a keyframe at the current frame would immediately re-apply its
        // value on the next scrub, fighting the user's drag.
        this.pivotStartHandle = this.events.on('pivot.started', () => { this.paused = true; });
        this.pivotEndHandle = this.events.on('pivot.ended', () => { this.paused = false; });
    }

    /** Wire up timeline listeners. Called the first time a key is added. */
    private subscribeIfNeeded(): void {
        if (this.timeHandle) return;
        this.timeHandle = this.events.on('timeline.time', (time: number) => this.evaluate(time));
        this.frameHandle = this.events.on('timeline.frame', (frame: number) => this.evaluate(frame));
        this.smoothHandle = this.events.on('timeline.smoothness', () => this.rebuildSpline());
    }

    /** Tear down timeline listeners when no keyframes remain. Also resets
     *  the target to the identity value (if one was declared) so removing
     *  all keys cleanly restores the "no effect" state — important for
     *  shader-driven effects where the last interpolated value would
     *  otherwise stick (e.g. revealRadius=0 → splats invisible forever). */
    private unsubscribeIfEmpty(): void {
        if (this.frames.length > 0) return;
        const wasSubscribed = !!this.timeHandle;
        if (this.timeHandle) {
            this.timeHandle.off(); this.timeHandle = null;
            this.frameHandle.off(); this.frameHandle = null;
            this.smoothHandle.off(); this.smoothHandle = null;
        }
        if (this.identity) {
            try {
                this.adapter.set(this.identity.slice());
                this.onApply?.();
            } catch {}
        }
        // Fire deactivate only on the empty transition (matches addKey's
        // wasEmpty check) — not on every removeKey of an already-empty track.
        if (wasSubscribed) {
            try { this.onDeactivateCb?.(); } catch {}
        }
    }

    // -------- AnimTrack interface --------

    get keys(): readonly number[] {
        return this.frames.map(f => f.frame);
    }

    addKey(frame: number): boolean {
        const wasEmpty = this.frames.length === 0;
        const value = this.adapter.get();
        const existingIdx = this.frames.findIndex(f => f.frame === frame);
        if (existingIdx === -1) {
            this.frames.push({ frame, value });
            this.frames.sort((a, b) => a.frame - b.frame);
            this.rebuildSpline();
            this.subscribeIfNeeded();
            if (wasEmpty) { try { this.onActivateCb?.(); } catch {} }
            this.events.fire('track.keyAdded', frame);
        } else {
            this.frames[existingIdx].value = value;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', frame);
        }
        return true;
    }

    removeKey(frame: number): boolean {
        const idx = this.frames.findIndex(f => f.frame === frame);
        if (idx === -1) return false;
        this.frames.splice(idx, 1);
        this.rebuildSpline();
        this.unsubscribeIfEmpty();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;
        const idx = this.frames.findIndex(f => f.frame === fromFrame);
        if (idx === -1) return false;

        // Drop any keyframe sitting on the destination — drag-on-top replaces.
        const toIdx = this.frames.findIndex(f => f.frame === toFrame);
        if (toIdx !== -1 && toIdx !== idx) {
            this.frames.splice(toIdx, 1);
        }

        const moved = this.frames.find(f => f.frame === fromFrame);
        if (moved) moved.frame = toFrame;
        this.frames.sort((a, b) => a.frame - b.frame);
        this.rebuildSpline();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;
        const src = this.frames.find(f => f.frame === fromFrame);
        if (!src) return false;

        const toIdx = this.frames.findIndex(f => f.frame === toFrame);
        const copy: Keyframe = { frame: toFrame, value: src.value.slice() };
        if (toIdx === -1) {
            this.frames.push(copy);
            this.frames.sort((a, b) => a.frame - b.frame);
        } else {
            this.frames[toIdx] = copy;
        }
        this.rebuildSpline();
        this.subscribeIfNeeded();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    clear(): void {
        if (this.frames.length === 0) return;
        this.frames = [];
        this.spline = null;
        this.unsubscribeIfEmpty();
        this.events.fire('track.keysCleared');
    }

    snapshot(): unknown {
        // Deep copy so subsequent edits don't mutate the snapshot.
        return this.frames.map(f => ({ frame: f.frame, value: f.value.slice() }));
    }

    restore(snapshot: unknown): void {
        const snap = snapshot as Keyframe[];
        this.frames = snap.map(f => ({ frame: f.frame, value: f.value.slice() }));
        this.rebuildSpline();
        if (this.frames.length > 0) this.subscribeIfNeeded();
        else this.unsubscribeIfEmpty();
        this.events.fire('track.keysLoaded');
    }

    // -------- evaluation --------

    /**
     * Compute interpolated value at `time` (in frames) and apply to target.
     * No-op when track has no keyframes (so the user can manipulate the
     * target directly without the track fighting them).
     */
    evaluate(time: number): void {
        if (this.frames.length === 0) return;
        // Do not fight the user while they're dragging a transform gizmo.
        if (this.paused) return;

        if (this.interp === 'step') {
            // Step: find latest keyframe at or before this time.
            let chosen = this.frames[0];
            for (const f of this.frames) {
                if (f.frame <= time) chosen = f;
                else break;
            }
            this.adapter.set(chosen.value);
        } else if (this.interp === 'linear' || this.frames.length < 2) {
            // Linear (or single-key: just hold).
            if (this.frames.length === 1) {
                this.adapter.set(this.frames[0].value);
            } else {
                const seg = this.findSegment(time);
                const a = this.frames[seg];
                const b = this.frames[seg + 1];
                const t = (time - a.frame) / Math.max(1e-6, b.frame - a.frame);
                const tc = Math.max(0, Math.min(1, t));
                for (let i = 0; i < this.adapter.dim; i++) {
                    this.scratch[i] = a.value[i] * (1 - tc) + b.value[i] * tc;
                }
                this.adapter.set(this.scratch);
            }
        } else {
            // Cubic spline — preserves smooth tangents through keyframes.
            if (!this.spline) this.rebuildSpline();
            if (this.spline) {
                this.spline.evaluate(time, this.scratch);
                this.adapter.set(this.scratch);
            }
        }
        this.onApply?.();
    }

    private findSegment(time: number): number {
        // Return idx s.t. frames[idx].frame <= time < frames[idx+1].frame.
        // Clamped at endpoints.
        if (time <= this.frames[0].frame) return 0;
        if (time >= this.frames[this.frames.length - 1].frame) return this.frames.length - 2;
        let lo = 0;
        let hi = this.frames.length - 1;
        while (lo + 1 < hi) {
            const mid = (lo + hi) >> 1;
            if (this.frames[mid].frame <= time) lo = mid;
            else hi = mid;
        }
        return lo;
    }

    private rebuildSpline(): void {
        if (this.interp !== 'cubic' || this.frames.length < 2) {
            this.spline = null;
            return;
        }
        const times = this.frames.map(f => f.frame);
        const points: number[] = [];
        for (const f of this.frames) {
            for (let i = 0; i < this.adapter.dim; i++) {
                points.push(f.value[i]);
            }
        }
        const smoothness = (this.events.invoke('timeline.smoothness') as number) ?? 1;
        this.spline = CubicSpline.fromPoints(times, points, smoothness);
    }

    // -------- serialization --------

    docSerialize(): unknown {
        return {
            id: this.id,
            interp: this.interp,
            keys: this.frames.map(f => ({ frame: f.frame, value: f.value.slice() }))
        };
    }

    docDeserialize(data: any): void {
        if (!data || !Array.isArray(data.keys)) return;
        this.frames = data.keys.map((k: any) => ({
            frame: k.frame,
            value: Array.isArray(k.value) ? k.value.slice() : [k.value]
        }));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }
}

export { PropertyAnimTrack };
